import React from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { NOTIFY_RESILIA_TO_ADD_GOAL } from 'Apollo/Mutations/NotifyResiliaToAddGoal'
import { useAppState } from 'Context/AppContext/AppContext'
import { useSnackbar } from 'Context/SnackbarContext/SnackbarContext'
import {
  Goal,
  GoalGroupCard as GoalGroupCardType,
  GoalSharedToFunderCard as GoalSharedToFunderCardType,
} from 'Generated/graphql-types'
import { SuperLoading } from 'Components/SharedComponents/Loading/SuperLoading'
import { Button, Loading } from '@resiliatech/birdsong'
import { FunderGoalsEmptyState } from 'Components/Pages/FunderDashboard/FunderGoals/EmptyStates/FunderGoalsEmptyState'
import { Typography } from '@material-ui/core'
import { GoalGroupCard } from 'Components/Pages/FunderDashboard/FunderGoals/GoalGroupCard'
import { GOAL_SHARED_TO_FUNDER_CARD } from 'Apollo/Queries/GoalSharedToFunderCard'
import { GOAL_GROUP_CARDS } from 'Apollo/Queries/GoalGroupCards'
import { funderGoals } from 'Modules/Analytics/trackActionInterface/FunderPlatform/funderGoals'
import { analytics } from 'Modules/Analytics'
import { useAddGoalLastClicked } from './AddGoalNotificationHook'
import {
  Actions,
  Content,
  FunderGoalsContainer,
  GoalsList,
  Header,
  HeaderContainer,
} from './FunderGoals.styled'
import { EmptyGoalGroupCard } from './EmptyGoalGroupCard'
import { GoalSharedToFunderCard } from './GoalSharedToFunderCard'
import { ADD_GOAL_DISABLED_COPY } from './utils'

const FunderGoals = () => {
  const [isAddGoalDisabled, setNextEnableDate] = useAddGoalLastClicked()
  const { popToast } = useSnackbar()
  const {
    userConfig: { id: userId },
    funderOrgConfig: { orgId: funderOrgId },
    userConfig: { id, email },
  } = useAppState()

  const [notifyResiliaToAddGoal, { loading: emailLoading }] = useMutation(
    NOTIFY_RESILIA_TO_ADD_GOAL,
    {
      variables: { userId },
    }
  )

  const {
    data: { goalGroupCards: goalGroupData = [] } = {},
    loading: goalsLoading,
  } = useQuery(GOAL_GROUP_CARDS, {
    variables: { funderOrgId },
    skip: !funderOrgId,
  })

  const { data: { goalSharedToFunderCard: goalData = [] } = {}, loading } =
    useQuery(GOAL_SHARED_TO_FUNDER_CARD, {
      variables: { activeOnly: true, funderOrgId },
      skip: !funderOrgId,
    })

  const dispatchAddGoalClicked = () => {
    analytics.trackActionCreator(funderGoals.Funder_Add_Goal_Clicked)({
      userId: id,
      userEmail: email,
      funderOrgId,
    })
  }

  const handleAddGoalClicked = async () => {
    const response = await notifyResiliaToAddGoal()
    dispatchAddGoalClicked()
    const wasEmailSent = response.data?.notifyResiliaToAddGoal
    if (wasEmailSent) {
      popToast({
        content: `Request sent! Someone from Resilia will reach out to you directly.`,
      })
      setNextEnableDate()
    } else {
      popToast({
        content: 'Something went wrong! Please try your request again.',
        options: { severity: 'error' },
      })
    }
  }

  const goalGroups: GoalGroupCardType[] = goalGroupData
  const goals: GoalSharedToFunderCardType[] = goalData

  const goalCards = goals.map((goal) => {
    const { targetProgress } = goal
    return (
      <GoalSharedToFunderCard
        key={goal.id}
        goal={goal as Goal}
        targetProgress={targetProgress}
      />
    )
  })

  const groupGoalCards = goalGroups.map((goalGroup) => {
    if (goalGroup.numGrantees === 0)
      return (
        <EmptyGoalGroupCard
          key={goalGroup.goalGroupId}
          isAddGoalDisabled={isAddGoalDisabled}
          onAddGoal={handleAddGoalClicked}
          goalGroup={goalGroup}
        />
      )
    return <GoalGroupCard key={goalGroup.goalGroupId} goalGroup={goalGroup} />
  })

  const cardsToRender = [...goalCards, ...groupGoalCards]
  const hasGoals = goalGroups.length > 0 || goals.length > 0

  if (goalsLoading || loading) return <SuperLoading />

  return (
    <FunderGoalsContainer>
      {hasGoals && (
        <Content>
          <HeaderContainer>
            <Header>Goals</Header>
            <Actions>
              <Button
                isLoading={emailLoading}
                isDisabled={isAddGoalDisabled}
                label={isAddGoalDisabled ? ADD_GOAL_DISABLED_COPY : 'Add Goal'}
                onClick={handleAddGoalClicked}
                variant='primary'
              />
            </Actions>
          </HeaderContainer>
          <Typography variant='h6'>
            Align with your grantees to maximize and track your collective
            impact.
          </Typography>
        </Content>
      )}
      {!!cardsToRender.length && <GoalsList>{cardsToRender}</GoalsList>}
      {goalsLoading && !hasGoals && <Loading height='500px' />}
      {!hasGoals && (
        <FunderGoalsEmptyState
          isAddGoalDisabled={isAddGoalDisabled}
          onAddGoal={handleAddGoalClicked}
        />
      )}
    </FunderGoalsContainer>
  )
}

export default FunderGoals
